import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { Z } from "../../../theme/zIndex";
import {
  Box,
  Typography,
  DialogContent,
  useTheme,
  IconButton,
  Fab,
  Alert as MuiAlert,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { alpha } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

import ReservationsListNew from "./ReservationsListNew";
import { ReservationVoucher } from "..";
import {
  LazyEditReservationModal,
  LazyAdminReservationModal,
} from "../../shared/components/LazyModals";
import { usePermissions } from "../../auth/hooks/usePermissions";
import { useEditRestriction } from "../../auth/hooks/useEditRestriction";
import RestrictionModal from "../../auth/components/RestrictionModal";
import { Reservation } from "../types/Reservation";
import { useZonas } from "../../calendar/hooks/useZonas";
import { useReservations } from "../../../hooks/useReservations";
import { useDeleteReservation } from "../../../hooks/useReservationMutations";
import { useQueryClient } from "@tanstack/react-query";
import { logger } from "../../../utils/logger";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { es } from "date-fns/locale";
import { DateRange } from "./ReservationFilters";
import { useNotify } from "../../../context/notifications";
import AppDialog from "../../shared/components/AppDialog";
import { formatDateKey } from "../../shared/utils/dateUtils";

const Reservations: React.FC = () => {
  const theme = useTheme();
  const { notify } = useNotify();

  const [filters, setFilters] = useState({
    page: 1,
    per_page: 25,
    rango: "todas",
    search: "",
    origen: "",
    saldo: "",
    departamento: "",
    movimiento: "",
    orden: "entrada_desc",
    fecha_desde: "",
    fecha_hasta: "",
  });

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    return {
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
      key: "selection",
    };
  });

  useEffect(() => {
    if (filters.fecha_desde && filters.fecha_hasta) {
      const startDate = new Date(filters.fecha_desde + "T00:00:00");
      const endDate = new Date(filters.fecha_hasta + "T00:00:00");

      setDateRange({
        startDate,
        endDate,
        key: "selection",
      });
    }
  }, [filters.fecha_desde, filters.fecha_hasta]);

  const {
    data: reservationsData,
    isLoading: loading,
    isError: _isError,
    error: queryError,
  } = useReservations(filters);

  const reservations = reservationsData?.reservas || [];
  const pagination = reservationsData?.pagination || {
    page: 1,
    per_page: 25,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false,
  };
  const summary = reservationsData?.summary;

  const error = queryError
    ? (queryError as Error)?.message || "Error al cargar las reservas"
    : null;

  const [editMode, setEditMode] = useState(false);
  const [selectedReservation, setSelectedReservation] =
    useState<Reservation | null>(null);
  const [openVoucher, setOpenVoucher] = useState(false);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const { zonas: _zonas, error: _errorZonas } = useZonas();
  const permissions = usePermissions();

  const editRestrictionHook = useEditRestriction();

  const deleteReservationMutation = useDeleteReservation();
  const queryClient = useQueryClient();

  const handleEdit = useCallback(
    (reservation: Reservation) => {
      if (!permissions.canEditReservations) {
        return;
      }
      setSelectedReservation(reservation);
      setEditMode(true);
    },
    [permissions.canEditReservations]
  );

  const handleDelete = useCallback(
    async (reservation: Reservation) => {
      logger.debug("Iniciando eliminación de reserva:", reservation.id);

      if (deleteReservationMutation.isPending) {
        logger.debug("Ya hay una eliminación en progreso, saltando");
        return;
      }

      const recentlyDeleted = queryClient.getQueryData(["recently-deleted"]) as
        | number
        | undefined;
      if (recentlyDeleted && Date.now() - recentlyDeleted < 5000) {
        logger.debug("Reserva eliminada recientemente, saltando");
        return;
      }

      try {
        await deleteReservationMutation.mutateAsync(reservation.id);
        logger.debug("Reserva eliminada exitosamente");
      } catch (err) {
        const status = (err as { response?: { status?: number } }).response
          ?.status;
        if (status !== 404) {
          logger.error("Error al eliminar la reserva:", err);
          notify("No se pudo eliminar la reserva. Intentá nuevamente.", {
            severity: "error",
          });
        } else {
          logger.debug(
            "Reserva ya eliminada (404), considerando operación exitosa"
          );
        }
      }
    },
    [deleteReservationMutation, notify, queryClient]
  );

  const handleVoucher = useCallback((reservation: Reservation) => {
    setSelectedReservation(reservation);
    setOpenVoucher(true);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditMode(false);
    setSelectedReservation(null);
  }, []);

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setFilters((prev) => ({ ...prev, per_page: pageSize, page: 1 }));
  };

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearch = useCallback((search: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setFilters((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleDateRangeChange = useCallback(
    (startDate: Date, endDate: Date) => {
      const fecha_desde = formatDateKey(startDate);
      const fecha_hasta = formatDateKey(endDate);

      setDateRange({
        startDate,
        endDate,
        key: "selection",
      });

      setFilters((prev) => ({
        ...prev,
        fecha_desde,
        fecha_hasta,
        page: 1,
      }));
    },
    []
  );

  const handleQuickFiltersChange = useCallback(
    (quickFilters: {
      origen: string;
      saldo: string;
      departamento: string;
      movimiento: string;
      orden: string;
    }) => {
      setFilters((prev) => ({
        ...prev,
        ...quickFilters,
        page: 1,
      }));
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setDateRange({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
      key: "selection",
    });

    setFilters({
      page: 1,
      per_page: 25,
      rango: "default",
      search: "",
      origen: "",
      saldo: "",
      departamento: "",
      movimiento: "",
      orden: "entrada_desc",
      fecha_desde: "",
      fecha_hasta: "",
    });
  }, []);

  const totalReservas = pagination.total ?? reservations.length;
  const periodoLabel =
    filters.fecha_desde && filters.fecha_hasta
      ? `${format(dateRange.startDate, "dd/MM/yyyy")} - ${format(
          dateRange.endDate,
          "dd/MM/yyyy"
        )}`
      : format(dateRange.startDate, "MMMM yyyy", { locale: es });

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        maxWidth: "100%",
        height: "100%",
        px: { xs: 1, sm: 2 },
        py: { xs: 1, sm: 1.5 },
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        bgcolor: theme.palette.background.default,
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Reservas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loading
              ? "Cargando reservas..."
              : `${totalReservas} ${
                  totalReservas === 1 ? "reserva" : "reservas"
                } · ${periodoLabel}`}
          </Typography>
        </Box>
      </Box>

      {loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "calc(100dvh - 64px)",
            bgcolor: "background.default",
            p: 3,
          }}
        >
          <CircularProgress size={60} color="primary" sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Cargando reservas...
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Ha ocurrido un error al cargar las reservas: {error}. Por favor,
          intente recargar la página.
        </Alert>
      )}

      {_errorZonas && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {_errorZonas}
        </Alert>
      )}

      {permissions.isRestrictedUser && (
        <MuiAlert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight={500}>
            {permissions.restrictionMessage}
          </Typography>
        </MuiAlert>
      )}

      {!loading && !error && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ReservationsListNew
            reservations={reservations}
            onReservationUpdate={() => {}}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onVoucher={handleVoucher}
            permissions={permissions}
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSearch={handleSearch}
            onDateRangeChange={handleDateRangeChange}
            onClearFilters={handleClearFilters}
            quickFilters={{
              origen: filters.origen,
              saldo: filters.saldo,
              departamento: filters.departamento,
              movimiento: filters.movimiento,
              orden: filters.orden,
            }}
            onQuickFiltersChange={handleQuickFiltersChange}
            summary={summary}
            dateRange={dateRange}
            editRestrictionHook={editRestrictionHook}
            RestrictionModalComponent={RestrictionModal}
          />
        </Box>
      )}

      {/* --- Modal de Edición --- */}
      {editMode && selectedReservation && (
        <LazyEditReservationModal
          open={editMode}
          onClose={handleCloseEdit}
          onSuccess={(_updatedReservation: Reservation) => {
            handleCloseEdit();
          }}
          selectedReservation={selectedReservation}
          existingReservations={reservations}
        />
      )}

      {/* --- Modal de Comprobante (Voucher) --- */}
      {openVoucher && selectedReservation && (
        <AppDialog
          open={openVoucher}
          onClose={() => setOpenVoucher(false)}
          maxWidth="md"
          PaperProps={{
            style: {
              backgroundColor: "transparent",
              boxShadow: "none",
              overflow: "visible",
            },
          }}
          sx={{
            "& .MuiDialogContent-root": {
              padding: 0,
              overflow: "visible",
            },
            "& .MuiBackdrop-root": {
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              backdropFilter: "blur(5px)",
            },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => setOpenVoucher(false)}
            sx={{
              position: "absolute",
              right: { xs: 8, sm: 16 },
              top: { xs: 8, sm: 16 },
              color: "white",
              zIndex: Z.modal,
              bgcolor: alpha(theme.palette.grey[700], 0.5),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[800], 0.7),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            <ReservationVoucher
              reserva={selectedReservation}
              onClose={() => setOpenVoucher(false)}
            />
          </DialogContent>
        </AppDialog>
      )}

      {/* --- Modal de Admin --- */}
      <LazyAdminReservationModal
        open={adminModalOpen}
        onClose={() => setAdminModalOpen(false)}
        existingReservations={reservations}
        onReservationUpdate={() => {
        }}
      />

      {permissions.isSuperAdmin && (
        <Tooltip
          title="Administración avanzada - solo superadmin"
          placement="left"
          arrow
        >
          <Fab
            color="primary"
            aria-label="Administración avanzada"
            onClick={() => setAdminModalOpen(true)}
            sx={{
              position: "fixed",
              bottom: 80,
              right: 24,
              background:
                "linear-gradient(135deg, #455a64 0%, #607d8b 100%)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, #37474f 0%, #546e7a 100%)",
                transform: "scale(1.04)",
              },
              boxShadow: 4,
              zIndex: Z.appBar,
            }}
          >
            <AdminPanelSettingsIcon />
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
};

export default Reservations;
